import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { create as createRule } from "../../services/rules";
import { getAxiosResponseError } from "../../utils/functions";
import {
  clearAdvancedSearch,
  setAdvancedSearchRuleId,
  useSelectAdvancedSearchRuleId,
} from "../../redux/features/advancedSearch/advancedSearchSlice";

/**
 * Page-level logic for AdvancedSearch. The /advanced-search route has
 * NO :id parameter — when the user navigates in we mint a transient
 * Rule of type ADVANCED_SEARCH so QueryBuilder has something to load.
 *
 * The id is then stored in the page-local slice so re-renders don't
 * re-mint. Strict-mode-safe via a ref guard.
 */
export function useAdvancedSearch() {
  const dispatch = useDispatch();
  const ruleId = useSelectAdvancedSearchRuleId();
  const requestedRef = useRef(false);

  const createMutation = useMutation({
    mutationFn: createRule,
    onSuccess: (data) => {
      dispatch(setAdvancedSearchRuleId(data?.id));
    },
    onError: (err) => {
      toast.error(getAxiosResponseError(err));
    },
  });

  useEffect(() => {
    // Only call create once per page mount. React 18 strict mode mounts
    // effects twice, so the ref guard plus the slice-stored id prevents
    // a double-mint.
    if (ruleId || requestedRef.current) return;
    requestedRef.current = true;
    createMutation.mutate({
      name: "",
      type: "ADVANCED_SEARCH",
      typeOfRule: "PTM",
    });
    // We intentionally don't depend on createMutation — `.mutate` is
    // stable and re-running this effect would mint again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleId]);

  useEffect(() => {
    return () => {
      dispatch(clearAdvancedSearch());
    };
  }, [dispatch]);

  return {
    ruleId,
    isCreating: createMutation.isPending,
    createError: createMutation.error,
  };
}
